import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { createCard, getUserCards, getUserConfig } from "../api/api";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { UserCard } from "../api/types";
import { CreditProviderId, getCreditProvider } from "../config/creditRegistry";
import { getCardSolverConfig } from "../api/utils";

/**
 * Creates a new user card if one does not already exist, signs the operation,
 * and updates the local card store with the created card.
 *
 * This function performs the following steps:
 * 1. Fetches existing user cards and updates the card store.
 * 2. If no card exists, signs a typed message and creates a new card.
 * 3. Verifies the card creation by fetching updated cards.
 * 4. Ensures that the newly created card belongs to the user (EOA).
 * 5. Updates the store with the new card if creation was successful.
 *
 * @param {CreateCardActionParams} params - Parameters for creating the card.
 * @param {CreditType} params.creditType - Type of credit to be associated with the card.
 *
 * @returns {Promise<{ success: boolean; message: string }>} - Result indicating success or failure and a message.
 */
export async function createCardAction({
  creditType,
}: {
  creditType: CreditProviderId;
}): Promise<{ success: boolean; message: string }> {
  const { jwt } = useSDKConfigStore.getState();
  const cardStatusStore = useCardStatusStore.getState();

  if (!jwt?.eoa) {
    return {
      success: false,
      message: "EOA address not found. Please initialize the SDK first.",
    };
  }

  try {
    // Step 0: Fetch user cards and set the initial one in store
    const userCardsResponse = await getUserCards();
    const initialUserCard = userCardsResponse.data?.[0] || null;
    cardStatusStore._setUserCard(initialUserCard);
    let card: UserCard | null = initialUserCard;

    const creditProvider = getCreditProvider(creditType);

    if (!creditProvider) {
      return {
        success: false,
        message: `Unsupported credit type: ${creditType}`,
      };
    }

    const userSpendModeConfig = await getUserConfig();

    const cardSolverConfig =
      creditProvider.creditType === "borrow"
        ? getCardSolverConfig(creditType)
        : userSpendModeConfig?.config ?? getCardSolverConfig(creditType);

    // Step 1: If no card exists, create one
    if (!card) {
      console.log("[createCardAction] Creating card...");
      const createCardResponse = await createCard(
        jwt.eoa,
        creditProvider.chainId,
        creditType,
        cardSolverConfig
      );
      card = { ...createCardResponse.data, cardID: createCardResponse.data.id };

      if (!card?.cardID) {
        return {
          success: false,
          message: "Failed to create card - invalid card ID received.",
        };
      }
    }

    // Step 4: Verify that the card was actually created
    console.log("[createCardAction] Step 4: Verifying card creation result...");
    const userCardsResponse2 = await getUserCards();
    const updatedCard = userCardsResponse2.data?.find(
      (c) => c?.cardID === card?.cardID
    );

    // Update the store with the latest card
    cardStatusStore._setUserCard(updatedCard || null);

    if (!updatedCard) {
      return {
        success: false,
        message:
          "Card not found after creation - card creation may have failed.",
      };
    }

    if (
      updatedCard.solverCreditProviderAddress.toLowerCase() !==
      jwt.eoa.toLowerCase()
    ) {
      return {
        success: false,
        message:
          "Card creation failed - credit provider address does not match EOA address.",
      };
    }

    console.log("[createCardAction] Card creation completed successfully.");
    return {
      success: true,
      message: "Card created and linked successfully.",
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[createCardAction] Error during card creation:", err);
    return {
      success: false,
      message,
    };
  }
}
